import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import SendIcon from "@material-ui/icons/Send";

import TableRowSkeleton from "../../components/TableRowSkeleton";
import { StatCard, StatusBadge } from "../../components/ui";
import { usePanelStyles } from "../../components/QueueIntegrationModal/panelKit";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";
import { getTokens } from "../../styles/tokens";
import useHotspotStatus from "../../hooks/useHotspotStatus";

import { ROWS_PER_PAGE, formatarDuracao, copyToClipboard, statusLabel } from "./helpers";
import NewBatchModal from "./NewBatchModal";
import BatchDetailDrawer from "./BatchDetailDrawer";
import SendVoucherDialog from "../../components/SendVoucherDialog";

const COLUMNS = 7;

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    totals: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: 12,
      marginBottom: 16,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
    },
    table: { minWidth: 820 },
    pagination: { borderTop: `1px solid ${t.line}` },
  };
});

const isPending = (status) => String(status || "").toLowerCase() === "aguardando_pagamento";

const VoucherBatchesTab = () => {
  const classes = useStyles();
  const panel = usePanelStyles();
  const { datetimeToClient } = useDate();
  const { baseUrl } = useHotspotStatus();

  const [batches, setBatches] = useState([]);
  const [totais, setTotais] = useState({ lotes: 0, fichas: 0, disponivel: 0, usado: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [devices, setDevices] = useState([]);

  const [newBatchOpen, setNewBatchOpen] = useState(false);
  const [detailBatch, setDetailBatch] = useState(null);
  const [sendBatch, setSendBatch] = useState(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchBatches = useCallback(async (query, pageIndex) => {
    setLoading(true);
    try {
      const { data } = await api.get("/hotspot/voucher-batches", {
        params: { q: query || undefined, take: ROWS_PER_PAGE, skip: pageIndex * ROWS_PER_PAGE },
      });
      if (!isMountedRef.current) return;
      setBatches(Array.isArray(data?.batches) ? data.batches : []);
      setTotal(Number(data?.total) || 0);
      setTotais({
        lotes: Number(data?.totais?.lotes) || 0,
        fichas: Number(data?.totais?.fichas) || 0,
        disponivel: Number(data?.totais?.disponivel) || 0,
        usado: Number(data?.totais?.usado) || 0,
      });
    } catch (err) {
      toastError(err);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBatches(q, page);
  }, [fetchBatches, q, page]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/hotspot/devices");
        if (active) setDevices(Array.isArray(data) ? data : []);
      } catch (err) {
        // silent: device select just falls back to "integration default"
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleSearchChange = (e) => {
    setPage(0);
    setQ(e.target.value);
  };

  const handleCreated = () => {
    fetchBatches(q, 0);
    setPage(0);
  };

  const handlePdf = async (batch) => {
    const win = window.open("", "_blank", "noopener,noreferrer");
    try {
      const res = await api.get(`/hotspot/voucher-batches/${batch.id}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (win) {
        win.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `fichas-${batch.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (win) win.close();
      toastError(err);
      toast.error(i18n.t("hotspot.toasts.pdfError"));
    }
  };

  const handleCopyLink = async (batch) => {
    const link = batch?.venda?.link || (batch.vendaToken && baseUrl ? `${baseUrl}/cobranca/${batch.vendaToken}` : null);
    if (!link) {
      toast.error(i18n.t("hotspot.errors.noLink"));
      return;
    }
    const ok = await copyToClipboard(link);
    if (ok) toast.success(i18n.t("hotspot.toasts.linkCopied"));
  };

  return (
    <>
      <NewBatchModal
        open={newBatchOpen}
        devices={devices}
        onClose={() => setNewBatchOpen(false)}
        onCreated={handleCreated}
      />
      <BatchDetailDrawer
        open={!!detailBatch}
        batchId={detailBatch?.id}
        batchName={detailBatch?.nome}
        onClose={() => setDetailBatch(null)}
      />
      <SendVoucherDialog open={!!sendBatch} batch={sendBatch} onClose={() => setSendBatch(null)} />

      <div className={classes.totals}>
        <StatCard tone="brand" label={i18n.t("hotspot.totals.batches")} value={totais.lotes} />
        <StatCard tone="brand" label={i18n.t("hotspot.totals.vouchers")} value={totais.fichas} />
        <StatCard tone="success" label={i18n.t("hotspot.totals.available")} value={totais.disponivel} />
        <StatCard tone="neutral" label={i18n.t("hotspot.totals.used")} value={totais.usado} />
      </div>

      <div className={panel.toolbar}>
        <TextField
          className={panel.toolbarGrow}
          placeholder={i18n.t("hotspot.search.placeholder")}
          value={q}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          inputProps={{ "aria-label": i18n.t("hotspot.search.placeholder") }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <div className={panel.toolbarSpacer} />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setNewBatchOpen(true)}
        >
          {i18n.t("hotspot.buttons.new")}
        </Button>
      </div>

      <Paper className={`${panel.tableCard} ${panel.tableCardAuto}`} elevation={0}>
        {!loading && batches.length === 0 ? (
          <div className={panel.empty}>{i18n.t("hotspot.table.empty")}</div>
        ) : (
          <>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("hotspot.table.name")}</TableCell>
                  <TableCell>{i18n.t("hotspot.table.device")}</TableCell>
                  <TableCell>{i18n.t("hotspot.table.duration")}</TableCell>
                  <TableCell align="right">{i18n.t("hotspot.table.available")}</TableCell>
                  <TableCell>{i18n.t("hotspot.table.status")}</TableCell>
                  <TableCell>{i18n.t("hotspot.table.createdAt")}</TableCell>
                  <TableCell align="right">{i18n.t("hotspot.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map((batch) => {
                  const pending = isPending(batch.status);
                  const contadores = batch.contadores || {};
                  return (
                    <TableRow key={batch.id}>
                      <TableCell className={panel.cellStrong}>{batch.nome}</TableCell>
                      <TableCell>
                        {batch.device ? batch.device.nome : i18n.t("hotspot.table.noDevice")}
                      </TableCell>
                      <TableCell>{formatarDuracao(batch.duracaoMin)}</TableCell>
                      <TableCell align="right">
                        {Number(contadores.disponivel) || 0} / {Number(contadores.usado) || 0}
                      </TableCell>
                      <TableCell>
                        <StatusBadge color={pending ? "warning" : "success"} label={statusLabel(batch.status)} />
                      </TableCell>
                      <TableCell>{batch.criadoEm ? datetimeToClient(batch.criadoEm) : "-"}</TableCell>
                      <TableCell align="right">
                        <div className={panel.rowActions}>
                          <Tooltip title={i18n.t("hotspot.buttons.viewVouchers")}>
                            <IconButton
                              size="small"
                              aria-label={i18n.t("hotspot.buttons.viewVouchers")}
                              onClick={() => setDetailBatch(batch)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={i18n.t("hotspot.buttons.pdf")}>
                            <IconButton
                              size="small"
                              aria-label={i18n.t("hotspot.buttons.pdf")}
                              onClick={() => handlePdf(batch)}
                            >
                              <PictureAsPdfIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {pending && (
                            <Tooltip title={i18n.t("hotspot.buttons.copyPixLink")}>
                              <IconButton
                                size="small"
                                aria-label={i18n.t("hotspot.buttons.copyPixLink")}
                                onClick={() => handleCopyLink(batch)}
                              >
                                <FileCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={i18n.t("hotspot.buttons.sendWhatsapp")}>
                            <IconButton
                              size="small"
                              aria-label={i18n.t("hotspot.buttons.sendWhatsapp")}
                              onClick={() => setSendBatch(batch)}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {loading && <TableRowSkeleton columns={COLUMNS} />}
              </TableBody>
            </Table>
            {total > ROWS_PER_PAGE && (
              <TablePagination
                className={classes.pagination}
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[]}
              />
            )}
          </>
        )}
      </Paper>
    </>
  );
};

export default VoucherBatchesTab;
